import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Alert,
  Avatar,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Divider,
} from '@mui/material';
import {
  ArrowBack,
  Email,
  CalendarToday,
  TrendingUp,
  Assignment,
  CheckCircle,
  Cancel,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import userAPI from '../../services/userAPI';
import statsAPI from '../../services/statsAPI';

const StudentDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [student, setStudent] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchStudentData();
  }, [id]);

  const fetchStudentData = async () => {
    try {
      setLoading(true);
      setError('');
      
      console.log('👤 Fetching student:', id);
      
      // Récupérer les infos de l'étudiant
      const userResponse = await userAPI.getById(id);
      console.log('✅ Student:', userResponse);
      
      const studentData = userResponse.user || userResponse.data?.user;
      setStudent(studentData);
      
      // Récupérer les statistiques
      const statsResponse = await statsAPI.getStudentStats(id);
      console.log('✅ Stats:', statsResponse);
      
      setStats(statsResponse.stats);
      
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Erreur lors du chargement des données';
      setError(errorMsg);
      toast.error(errorMsg);
      console.error('❌ Student detail error:', err);
    } finally {
      setLoading(false);
    }
  };

  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !student) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/students')}
          sx={{ mb: 2 }}
        >
          Retour
        </Button>
        <Alert severity="error">
          {error || 'Étudiant introuvable'}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate('/students')}
        sx={{ mb: 2 }}
      >
        Retour à la liste
      </Button>

      {/* Informations de l'étudiant */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 3 }}>
            <Avatar
              sx={{
                width: 80,
                height: 80,
                bgcolor: 'primary.main',
                fontSize: '2rem'
              }}
            >
              {getInitials(student.name)}
            </Avatar>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h4" gutterBottom>
                {student.name || 'Sans nom'}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Chip
                  icon={<Email />}
                  label={student.email}
                  variant="outlined"
                />
                <Chip
                  label={student.role}
                  color="secondary"
                  size="small"
                />
              </Box>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarToday fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  Inscrit le : {formatDate(student.createdAt)}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarToday fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  Dernière connexion : {formatDate(student.lastLogin)}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Statistiques */}
      {stats && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Assignment sx={{ fontSize: 40, color: 'primary.main' }} />
                  <Box>
                    <Typography variant="h4" color="primary">
                      {stats.totalAttempts}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Tentatives
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <CheckCircle sx={{ fontSize: 40, color: 'success.main' }} />
                  <Box>
                    <Typography variant="h4" color="success.main">
                      {stats.passedAttempts}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Réussies
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <TrendingUp sx={{ fontSize: 40, color: 'warning.main' }} />
                  <Box>
                    <Typography variant="h4" color="warning.main">
                      {stats.averageScore}%
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Score Moyen
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Cancel sx={{ fontSize: 40, color: 'error.main' }} />
                  <Box>
                    <Typography variant="h4" color="error.main">
                      {stats.failedAttempts}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Échecs
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Tentatives récentes */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Tentatives Récentes
          </Typography>
          
          {stats?.recentAttempts && stats.recentAttempts.length > 0 ? (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Quiz</TableCell>
                    <TableCell align="center">Score</TableCell>
                    <TableCell align="center">Résultat</TableCell>
                    <TableCell align="center">Date</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stats.recentAttempts.map((attempt) => (
                    <TableRow key={attempt._id} hover>
                      <TableCell>
                        <Typography variant="body2">
                          {attempt.quiz?.title || 'Quiz supprimé'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="h6" color="primary">
                          {attempt.score}%
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={attempt.passed ? 'Réussi' : 'Échoué'}
                          color={attempt.passed ? 'success' : 'error'}
                          size="small"
                          icon={attempt.passed ? <CheckCircle /> : <Cancel />}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(attempt.completedAt)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => navigate(`/quiz/${attempt.quiz._id}/results/${attempt._id}`)}
                        >
                          Voir Résultats
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
              Aucune tentative pour le moment
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Menu contextuel */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleViewDetails}>
          <Visibility sx={{ mr: 1 }} />
          Voir Détails
        </MenuItem>
        <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
          <Delete sx={{ mr: 1 }} />
          Supprimer
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default StudentDetail;
